// Akte eines Lieferanten: welche Ware von ihm kommt, was geliefert wurde,
// welche Rechnungen offen sind.
//
// Rechnungen und Beträge sieht nur ein Administrator — der Server liefert sie
// Mitarbeitern gar nicht erst aus (`finanzen: false`).
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum, menge as mengeText } from '../../../gemeinsam/lib/daten';
import { einheitAnzeige } from '../../artikel/lib/einheiten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import type { Lieferant } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Akte = {
  lieferant: Lieferant;
  finanzen: boolean;
  kennzahlen: {
    artikel: number; lieferungen: number; letzteLieferung: string | null;
    offen?: number; offenAnzahl?: number; ueberfaellig?: number; ueberfaelligAnzahl?: number; einkaufJahr?: number;
  };
  artikel: Array<{ id: string; name: string; artikelnummer: string; bestand: number; einheit: string; knapp: boolean; einkaufspreis?: number }>;
  wareneingaenge: Array<{ id: string; datum: string; produktName: string; menge: number; einheit: string; grund: string; wert?: number | null }>;
  rechnungen?: Array<{
    id: string; datum: string; belegnummer: string; bezeichnung: string; brutto: number;
    status: 'offen' | 'bezahlt'; faelligAm: string | null; bezahltAm: string | null; ueberfaellig: boolean;
  }>;
};

const heute = () => new Date().toISOString().slice(0, 10);
const inTagen = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const zahl = (s: string) => { const t = s.trim(); return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t) || 0; };

export default function LieferantAkte({ id, beiZurueck, beiBearbeiten }: {
  id: string; beiZurueck: () => void; beiBearbeiten: (l: Lieferant) => void;
}) {
  const { t } = useSprache();
  const { daten: akte, laedt, fehler, neuLaden } = useDaten<Akte>(`/suppliers/${id}/akte`, [id]);
  const [formular, setFormular] = useState<{ nummer: string; datum: string; brutto: string; steuersatz: string; faelligAm: string; bezahlt: boolean } | null>(null);
  const [meldung, setMeldung] = useState<{ art: 'erfolg' | 'fehler'; text: string } | null>(null);
  const [arbeitet, setArbeitet] = useState(false);

  if (laedt && !akte) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (fehler || !akte) return <Hinweis art="fehler">{fehler || 'Akte nicht gefunden.'}</Hinweis>;
  const { lieferant: l, kennzahlen: k } = akte;

  async function rechnungSpeichern(e: React.FormEvent) {
    e.preventDefault();
    if (!formular) return;
    setArbeitet(true); setMeldung(null);
    try {
      await api('/expenses', {
        methode: 'POST',
        daten: {
          kategorie: 'wareneinkauf', lieferantId: l.id, belegnummer: formular.nummer,
          bezeichnung: `Rechnung ${formular.nummer || l.name}`.trim(), datum: formular.datum,
          brutto: zahl(formular.brutto), steuersatz: Number(formular.steuersatz),
          status: formular.bezahlt ? 'bezahlt' : 'offen', faelligAm: formular.bezahlt ? undefined : formular.faelligAm,
        },
      });
      setFormular(null);
      setMeldung({ art: 'erfolg', text: t('lieferanten.gespeichert') });
      await neuLaden();
    } catch (err) {
      setMeldung({ art: 'fehler', text: err instanceof ApiFehler ? err.message : 'Speichern fehlgeschlagen.' });
    } finally { setArbeitet(false); }
  }

  async function zahlung(rechnungId: string, wieder = false) {
    setMeldung(null);
    try {
      await api(`/expenses/${rechnungId}/${wieder ? 'offen' : 'bezahlt'}`, { methode: 'POST', daten: {} });
      await neuLaden();
    } catch (err) {
      setMeldung({ art: 'fehler', text: err instanceof ApiFehler ? err.message : 'Das hat nicht geklappt.' });
    }
  }

  const kachel = (titel: string, wert: string, zusatz?: string, ton?: 'warnung') => (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{titel}</p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${ton === 'warnung' ? 'text-amber-700 dark:text-amber-400' : 'text-slate-900 dark:text-slate-100'}`}>{wert}</p>
      {zusatz && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{zusatz}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button type="button" onClick={beiZurueck} className="text-sm text-marke-700 hover:underline dark:text-marke-300">← {t('lieferanten.alle')}</button>
          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{l.name}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {[l.lieferantennummer, l.ansprechpartner, l.email, l.telefon, [l.plz, l.ort].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || '—'}
          </p>
        </div>
        <Knopf variante="still" onClick={() => beiBearbeiten(l)}>{t('lieferanten.bearbeiten')}</Knopf>
      </div>

      {meldung && <Hinweis art={meldung.art}>{meldung.text}</Hinweis>}

      <div className={`grid gap-3 ${akte.finanzen ? 'sm:grid-cols-4' : 'sm:grid-cols-2'}`}>
        {kachel(t('lieferanten.artikel'), String(k.artikel))}
        {kachel(t('lieferanten.lieferungen'), String(k.lieferungen), k.letzteLieferung ? `${t('lieferanten.letzteLieferung')}: ${datum(k.letzteLieferung)}` : undefined)}
        {akte.finanzen && kachel(t('lieferanten.offen'), euro(k.offen ?? 0),
          k.ueberfaelligAnzahl ? `${euro(k.ueberfaellig ?? 0)} ${t('lieferanten.ueberfaellig')}` : `${k.offenAnzahl ?? 0} ${t('lieferanten.rechnungen')}`,
          k.ueberfaelligAnzahl ? 'warnung' : undefined)}
        {akte.finanzen && kachel(t('lieferanten.einkaufJahr').replace('{jahr}', String(new Date().getFullYear())), euro(k.einkaufJahr ?? 0))}
      </div>

      {akte.finanzen && (
        <Karte>
          <KartenKopf titel={t('lieferanten.rechnungen')}
                      aktion={!formular && (
                        <Knopf variante="primaer" className="!text-xs"
                               onClick={() => setFormular({ nummer: '', datum: heute(), brutto: '', steuersatz: '19', faelligAm: inTagen(14), bezahlt: false })}>
                          {t('lieferanten.rechnungErfassen')}
                        </Knopf>
                      )} />
          {formular && (
            <form onSubmit={rechnungSpeichern} className="space-y-3 border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
              <div className="grid gap-3 sm:grid-cols-4">
                <Feld label={t('lieferanten.rechnungsnummer')} value={formular.nummer} autoFocus
                      onChange={e => setFormular(f => f && ({ ...f, nummer: e.target.value }))} />
                <Feld label={t('lieferanten.rechnungsdatum')} type="date" value={formular.datum} max={heute()} required
                      onChange={e => setFormular(f => f && ({ ...f, datum: e.target.value }))} />
                <Feld label={t('lieferanten.betrag')} value={formular.brutto} inputMode="decimal" required
                      onChange={e => setFormular(f => f && ({ ...f, brutto: e.target.value }))} />
                <Auswahl label="USt" value={formular.steuersatz} onChange={e => setFormular(f => f && ({ ...f, steuersatz: e.target.value }))}>
                  {['19', '7', '0'].map(x => <option key={x} value={x}>{x} %</option>)}
                </Auswahl>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={formular.bezahlt} onChange={e => setFormular(f => f && ({ ...f, bezahlt: e.target.checked }))} />
                  {t('lieferanten.schonBezahlt')}
                </label>
                {!formular.bezahlt && (
                  <div className="w-44">
                    <Feld label={t('lieferanten.faelligAm')} type="date" value={formular.faelligAm}
                          onChange={e => setFormular(f => f && ({ ...f, faelligAm: e.target.value }))} />
                  </div>
                )}
                <div className="ml-auto flex gap-2">
                  <Knopf type="submit" variante="primaer" laedt={arbeitet}>{t('aktion.speichern')}</Knopf>
                  <Knopf type="button" onClick={() => setFormular(null)}>{t('aktion.abbrechen')}</Knopf>
                </div>
              </div>
            </form>
          )}
          {!akte.rechnungen?.length ? <LeerZustand titel={t('lieferanten.keineRechnungen')} /> : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
              {akte.rechnungen.map(r => (
                <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <div className="min-w-[10rem] flex-1">
                    <p className="font-medium text-slate-900 dark:text-slate-100">{r.belegnummer || r.bezeichnung}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{datum(r.datum)}{r.belegnummer && r.bezeichnung ? ` · ${r.bezeichnung}` : ''}</p>
                  </div>
                  {r.status === 'bezahlt'
                    ? <Abzeichen art="gruen">{t('lieferanten.bezahltAm').replace('{datum}', datum(r.bezahltAm))}</Abzeichen>
                    : <Abzeichen art={r.ueberfaellig ? 'rot' : 'gelb'}>
                        {r.ueberfaellig ? `${t('lieferanten.ueberfaellig')} · ` : ''}{t('lieferanten.faellig').replace('{datum}', datum(r.faelligAm))}
                      </Abzeichen>}
                  <span className="w-24 text-right font-medium tabular-nums text-slate-900 dark:text-slate-100">{euro(r.brutto)}</span>
                  {r.status === 'offen'
                    ? <Knopf variante="primaer" className="!px-2.5 !py-1 !text-xs" onClick={() => zahlung(r.id)}>{t('lieferanten.alsBezahlt')}</Knopf>
                    : <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => zahlung(r.id, true)}>{t('lieferanten.wiederOffen')}</Knopf>}
                </li>
              ))}
            </ul>
          )}
        </Karte>
      )}

      <Karte>
        <KartenKopf titel={t('lieferanten.artikel')} />
        {!akte.artikel.length ? <LeerZustand titel={t('lieferanten.keineArtikel')} /> : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {akte.artikel.map(a => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">
                  {a.name}{a.artikelnummer && <span className="ml-2 text-xs text-slate-500">{a.artikelnummer}</span>}
                </span>
                {a.knapp && <Abzeichen art="gelb">{t('lager.knapp')}</Abzeichen>}
                <span className="tabular-nums text-slate-700 dark:text-slate-300">{mengeText(a.bestand, einheitAnzeige(a.einheit, t))}</span>
                {a.einkaufspreis !== undefined && <span className="w-24 text-right tabular-nums text-slate-500">{euro(a.einkaufspreis)}</span>}
              </li>
            ))}
          </ul>
        )}
      </Karte>

      <Karte>
        <KartenKopf titel={t('lieferanten.lieferungen')} />
        {!akte.wareneingaenge.length ? <LeerZustand titel={t('lieferanten.keineLieferungen')} /> : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {akte.wareneingaenge.map(w => (
              <li key={w.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                <span className="w-24 text-slate-500 dark:text-slate-400">{datum(w.datum)}</span>
                <span className="min-w-0 flex-1 truncate text-slate-900 dark:text-slate-100">{w.produktName}
                  {w.grund && <span className="ml-2 text-xs text-slate-500">{w.grund}</span>}</span>
                <span className="tabular-nums text-slate-700 dark:text-slate-300">+{mengeText(w.menge, einheitAnzeige(w.einheit, t))}</span>
                {w.wert !== undefined && <span className="w-24 text-right tabular-nums text-slate-500">{w.wert === null ? '—' : euro(w.wert)}</span>}
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
